import { useCallback, useMemo } from "react";
import { debug as debugFn } from "debug";
import { errAsync, ResultAsync } from "neverthrow";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import type {
  IncidentWorkItemWithContext,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
  workflows,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { useUser } from "../../Context/UserContext.js";
import type { PersonDetailsFormProps } from "../../forms/PersonDetails/PersonDetailsForm.js";
import {
  PersonDetailsForm,
  PersonDetailsFormSkeleton,
} from "../../forms/PersonDetails/PersonDetailsForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { createRoute } from "../../routing/createRoute.js";
import { Error as ErrorComponent } from "../Error/Error.js";

const debug = debugFn("vision-frontend:PersonDetailsPage");

export type PersonDetailsPageProps = Record<string, unknown>;

// This component is responsible for managing the API calls and passing
// the data to the inner component.
export function PersonDetailsPage(_props: PersonDetailsPageProps) {
  const params = useParams();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    { data: incidentWorkItem, error: incidentWorkItemError },
    refetchWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  const { user } = useUser();

  if (incidentWorkItemError || user === undefined) {
    return <ErrorComponent />;
  }

  return (
    <PersonDetailsPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

export type PersonDetailsPageInnerProps = {
  incidentWorkItem?: IncidentWorkItemWithContext;
  refetchWorkItem: ReturnType<typeof apiHooks.incidentWorkItem.get>[1];
};

export function PersonDetailsPageInner({
  incidentWorkItem,
  refetchWorkItem,
}: PersonDetailsPageInnerProps) {
  if (incidentWorkItem === undefined) {
    return <PersonDetailsPageInnerLoading />;
  }

  const hasSection136Form = incidentWorkItem.forms.some(
    (form) => form.formType === IncidentFormTypeSchema.enum.section136,
  );
  if (!hasSection136Form) {
    return (
      <Navigate
        replace
        to={createRoute.incidentProgressPage(
          incidentWorkItem.incidentWorkItem.id,
        )}
      />
    );
  }

  return (
    <PersonDetailsPageInnerLoaded
      incidentWorkItem={incidentWorkItem}
      refetchWorkItem={refetchWorkItem}
    />
  );
}

function PersonDetailsPageInnerLoading() {
  return <PersonDetailsFormSkeleton />;
}

export function PersonDetailsPageInnerLoaded({
  incidentWorkItem,
  refetchWorkItem,
}: Required<PersonDetailsPageInnerProps>) {
  const navigate = useNavigate();
  const toastNotifications = useToastNotifications();
  const { t } = useTranslation();

  const incidentWorkItemId = incidentWorkItem.incidentWorkItem.id;
  const workflowVersion = incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  const section136Form = useMemo(
    () =>
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.section136,
      ),
    [incidentWorkItem.forms],
  );
  if (!section136Form) {
    throw new Error("Section 136 form not found");
  }

  const section136FormId = section136Form.id;

  // Get the form specification (including pre-computed defaultValues)
  const formSpecification = useMemo(() => {
    const existingData =
      section136Form.sections.find(
        (section) => section.id === FormSectionSchema.enum.personDetails,
      )?.data ?? {};
    return workflows[workflowVersion]?.[136].formDefinition.sections
      .find((section) => section.id === FormSectionSchema.enum.personDetails)
      ?.buildFormSpecification({ incidentWorkItem, existingData });
  }, [workflowVersion, incidentWorkItem, section136Form.sections]);

  if (!formSpecification) {
    throw new Error("Form specification not found");
  }

  const { formSchema: schema, defaultValues: initialValues } =
    formSpecification;

  const existingValuesWereValid = useMemo(() => {
    const existingData =
      section136Form.sections.find(
        (section) => section.id === FormSectionSchema.enum.personDetails,
      )?.data ?? {};
    return schema.safeParse(existingData).success;
  }, [schema, section136Form.sections]);

  const enableAutoSave = useMemo(
    () =>
      workflows[workflowVersion]?.[136].formDefinition.sections.find(
        (section) => section.id === FormSectionSchema.enum.personDetails,
      )?.autoSave ?? false,
    [workflowVersion],
  );

  const onAutoSave: Exclude<
    PersonDetailsFormProps<typeof workflowVersion>["onAutoSave"],
    undefined
  > = useCallback(
    (values) => {
      if (existingValuesWereValid) {
        // Because we started off with a valid and complete form, we should not regress
        // to a partial form.
        const isValid = schema.safeParse(values).success;
        if (!isValid) {
          return errAsync("Blocked auto-save: form is not valid");
        }
      }
      return ResultAsync.fromPromise(
        api.incidentWorkItem.updateData(
          incidentWorkItemId,
          section136FormId,
          FormSectionSchema.enum.personDetails,
          values,
          existingValuesWereValid,
        ),
        (error) =>
          error instanceof Error ? error.message : JSON.stringify(error),
      )
        .orTee(debug)
        .map(() => true);
    },
    [incidentWorkItemId, existingValuesWereValid, schema, section136FormId],
  );

  const onSubmit: PersonDetailsFormProps<typeof workflowVersion>["onSubmit"] =
    useCallback(
      async (values: Record<string, unknown>) => {
        try {
          const response = await api.incidentWorkItem.updateData(
            incidentWorkItemId,
            section136FormId,
            FormSectionSchema.enum.personDetails,
            values,
            true,
          );
          if (response.status === 204) {
            await refetchWorkItem();
            navigate(`/incident/${incidentWorkItemId}/section-complete`, {
              state: { type: FormSectionSchema.enum.personDetails },
            });
            return;
          } else {
            throw new Error(JSON.stringify(response.data.message));
          }
        } catch (error) {
          debug("Failed to update Person Details section", error);
          toastNotifications.show(
            t("incidentWorkItem.notificationFailedToSave"),
            {
              severity: "error",
            },
          );
        }
      },
      [
        incidentWorkItemId,
        navigate,
        refetchWorkItem,
        section136FormId,
        t,
        toastNotifications,
      ],
    );

  const onCancel: PersonDetailsFormProps<typeof workflowVersion>["onCancel"] =
    useCallback(() => {
      navigate(createRoute.incidentProgressPage(incidentWorkItemId));
    }, [incidentWorkItemId, navigate]);

  return (
    <PersonDetailsForm
      onAutoSave={enableAutoSave ? onAutoSave : undefined}
      onSubmit={onSubmit}
      onCancel={onCancel}
      initialValues={initialValues}
      incidentWorkItem={incidentWorkItem}
      workflowVersion={workflowVersion}
    />
  );
}
